<template>
  <div class="lang-dropdown" ref="dropdownRef" :class="{ 'lang-dropdown--auth': auth }">
    <!-- Trigger Button -->
    <button
      class="lang-dropdown__trigger"
      :class="{ 'lang-dropdown__trigger--auth': auth }"
      @click="toggleOpen"
      :aria-label="$t('accessibilityLabels.selectLanguage')"
      :aria-expanded="isOpen"
      type="button"
      >
      <!-- Globe icon -->
      <span class="lang-dropdown__icon">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
          <path d="M12 2C12 2 8 7 8 12C8 17 12 22 12 22" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          <path d="M12 2C12 2 16 7 16 12C16 17 12 22 12 22" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          <path d="M2.5 9H21.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          <path d="M2.5 15H21.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
      </span>

      <!-- Current label -->
      <span class="lang-dropdown__label">{{ currentLabel }}</span>

      <!-- Chevron -->
      <span class="lang-dropdown__chevron" :class="{ 'lang-dropdown__chevron--open': isOpen }">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    </button>

    <!-- Dropdown Panel -->
    <Transition name="lang-fade">
      <div v-if="isOpen" class="lang-dropdown__panel" :class="{ 'lang-dropdown__panel--auth': auth }">
        <div class="lang-dropdown__panel-inner">
          <button
            v-for="option in options"
            :key="option.value"
            class="lang-dropdown__option"
            :class="{ 'lang-dropdown__option--active': selectedLocale === option.value }"
            @click="selectLocale(option.value)"
            type="button"
          >
            <span class="lang-dropdown__option-flag">{{ option.flag }}</span>
            <span class="lang-dropdown__option-text">{{ option.label }}</span>
            <span v-if="selectedLocale === option.value" class="lang-dropdown__option-check">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12L10 17L20 7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </button>
        </div>
      </div>
    </Transition>

    <!-- Global Full-Screen Loader for Language Switch -->
    <Teleport to="body">
      <div v-if="isLoading" class="fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm gap-4">
        <SpinnerLoader :size="64" color="#ffffff" />
        <span class="text-white font-bold text-lg tracking-wide">{{ t('commonLabels.updatingLanguage') || 'Updating Language...' }}</span>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Cookies from 'js-cookie';
import SpinnerLoader from '@/components/SpinnerLoader.vue';
import { nonAuthData, authData } from '@/api/apiService';
import { META_TRANSLATIONS, USER_PROFILE } from '@/api/apiRoute';
import { loadLanguage } from '@/i18n/index';
import { useUserStore } from '@/stores/userStore';

// Optional prop so parent can hint which context it's in
const props = defineProps({
  auth: {
    type: Boolean,
    default: false,
  },
});

const { locale, t } = useI18n();
const userStore = useUserStore();

const selectedLocale = ref(locale.value);
const isOpen = ref(false);
const isLoading = ref(false);
const dropdownRef = ref(null);

const options = ref([
  { value: 'en', label: 'English', flag: '' },
  { value: 'pt', label: 'Português', flag: '' }
]);

const currentLabel = computed(() => {
  // Use locale.value (the i18n source of truth) so the button always shows
  // the full language name (e.g. "Português") and never the raw code ("pt").
  return options.value.find((o) => o.value === locale.value)?.label ?? 'English';
});

function toggleOpen() {
  isOpen.value = !isOpen.value;
}

async function selectLocale(value) {
  selectedLocale.value = value;
  isOpen.value = false;
  isLoading.value = true;
  
  try {
    // Update cookie
    Cookies.set('language', value, { expires: 365 });
    
    // Call language loader again
    await loadLanguage(value);
    
    // PATCH /me with selectedLanguage if logged in
    if (userStore.accessToken || userStore.loginResponse?.access) {
      await authData.patch(USER_PROFILE, { selectedLanguage: value });
    }
  } catch (e) {
    console.error("Failed to update selectedLanguage", e);
  } finally {
    isLoading.value = false;
  }
}

function handleClickOutside(e) {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target)) {
    isOpen.value = false;
  }
}

// Fetch languages from API to populate dropdown dynamically
async function fetchLanguages() {
  try {
    const response = await nonAuthData.get(META_TRANSLATIONS);
    const languages = response?.data?.data || [];
    
    if (languages.length) {
      options.value = languages
        .filter(lang => lang.languageCode !== 'zz') // Filter out 'zz' language
        .map(lang => ({
          value: lang.languageCode,
          label: lang.display || lang.languageCode,
          flag: lang.flag || '' // optional if api provides it
        }));
    }
  } catch (error) {
    console.error("Failed to fetch languages for dropdown", error);
    options.value = [
      { value: 'en', label: t('layoutLabels.language.english') || 'English' },
      { value: 'pt', label: t('layoutLabels.language.portuguese') || 'Português' }
    ];
  }
}

// Keep selectedLocale in sync if locale changes externally (e.g. on app boot)
watch(locale, (newLocale) => {
  if (selectedLocale.value !== newLocale) {
    selectedLocale.value = newLocale;
  }
});

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
  fetchLanguages();
});
onUnmounted(() => document.removeEventListener('mousedown', handleClickOutside));
</script>

<style scoped>
/* ── Root ────────────────────────────────────────────── */
.lang-dropdown {
  position: relative;
  display: inline-block;
  font-family: inherit;
  z-index: 50;
}

/* ── Trigger Button ──────────────────────────────────── */
.lang-dropdown__trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 10px;
  border: 1.5px solid rgba(74, 194, 209, 0.35);
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #1a7a8a;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(74, 194, 209, 0.12);
}

.lang-dropdown__trigger:hover {
  border-color: rgba(74, 194, 209, 0.7);
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 4px 14px rgba(74, 194, 209, 0.22);
  transform: translateY(-1px);
}

/* Auth layout variant — solid white pill on the teal/cyan background */
.lang-dropdown__trigger--auth {
  background: #ffffff;
  border-color: rgba(74, 194, 209, 0.4);
  color: #1a7a8a;
  padding: 10px 18px;
  font-size: 14px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.14);
}

.lang-dropdown__trigger--auth:hover {
  background: #f0fbfd;
  border-color: rgba(74, 194, 209, 0.75);
  box-shadow: 0 6px 22px rgba(26, 122, 138, 0.22);
  transform: translateY(-1px);
}

/* ── Globe Icon ──────────────────────────────────────── */
.lang-dropdown__icon {
  display: flex;
  align-items: center;
  opacity: 0.85;
}

/* ── Label ──────────────────────────────────────────── */
.lang-dropdown__label {
  letter-spacing: 0.01em;
}

/* ── Chevron ────────────────────────────────────────── */
.lang-dropdown__chevron {
  display: flex;
  align-items: center;
  opacity: 0.7;
  transition: transform 0.22s ease;
}

.lang-dropdown__chevron--open {
  transform: rotate(180deg);
}

/* ── Dropdown Panel ─────────────────────────────────── */
.lang-dropdown__panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 160px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(18px);
  border: 1.5px solid rgba(74, 194, 209, 0.2);
  box-shadow:
    0 8px 32px rgba(26, 122, 138, 0.14),
    0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

/* Auth panel: fully opaque so it never bleeds into the teal/gradient background */
.lang-dropdown__panel--auth {
  background: #ffffff;
  border-color: rgba(74, 194, 209, 0.45);
  box-shadow:
    0 12px 40px rgba(0, 0, 0, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.1);
}

.lang-dropdown__panel-inner {
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* ── Option ─────────────────────────────────────────── */
.lang-dropdown__option {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 12px;
  border-radius: 9px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  width: 100%;
  text-align: left;
  transition: background 0.15s ease, color 0.15s ease;
}

.lang-dropdown__option:hover {
  background: rgba(74, 194, 209, 0.1);
  color: #1a7a8a;
}

.lang-dropdown__option--active {
  background: linear-gradient(135deg, rgba(74, 194, 209, 0.15), rgba(45, 170, 185, 0.1));
  color: #1a7a8a;
  font-weight: 700;
}

.lang-dropdown__option-flag {
  font-size: 16px;
  line-height: 1;
}

.lang-dropdown__option-text {
  flex: 1;
}

.lang-dropdown__option-check {
  display: flex;
  align-items: center;
  color: #1a9aad;
}

/* ── Transition ─────────────────────────────────────── */
.lang-fade-enter-active,
.lang-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.lang-fade-enter-from,
.lang-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.97);
}
</style>
